import { ShieldAlert, AlertTriangle, Info, Wand2, Download, X, CheckCircle2, Loader2, FileSearch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

const SEVERITY = {
  blocking: { label: 'Blocking', icon: ShieldAlert, bar: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200', text: 'text-red-700' },
  warning: { label: 'Warning', icon: AlertTriangle, bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200', text: 'text-amber-700' },
  info: { label: 'Info', icon: Info, bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 border-sky-200', text: 'text-sky-700' },
};

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const PreflightHeadline = ({ report }) => {
  const { counts, rows_checked, filename } = report;
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-testid="preflight-headline">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
          <FileSearch size={14} /> Preflight check · {filename}
        </p>
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#0F172A] mt-2" data-testid="preflight-headline-text">
          {rows_checked.toLocaleString()} rows checked — {plural(counts.blocking, 'blocking error')}, {plural(counts.warning, 'warning')}, {counts.info.toLocaleString()} info
        </h2>
      </div>
      <div className="flex gap-2">
        {Object.entries(SEVERITY).map(([k, s]) => (
          <span key={k} className={`px-3 py-1.5 border rounded-sm text-xs font-semibold ${s.chip}`} data-testid={`preflight-count-${k}`}>
            {counts[k]} {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const IssueCard = ({ issue, index }) => {
  const s = SEVERITY[issue.severity];
  const Icon = s.icon;
  return (
    <div className="relative bg-white border border-slate-200 rounded-sm overflow-hidden pf-card-in"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }} data-testid={`preflight-issue-${issue.rule}-${index}`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${s.bar}`} />
      <div className="p-5 pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <Icon size={18} className={`${s.text} mt-0.5 flex-shrink-0`} />
            <h3 className="text-sm font-semibold text-[#0F172A]">{issue.title}</h3>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {issue.autofixable && (
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                <Wand2 size={11} /> Auto-fixable
              </span>
            )}
            <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${s.chip}`}>{s.label}</span>
          </div>
        </div>
        <p className="text-sm text-slate-700 mt-2" data-testid="preflight-issue-message">{issue.message}</p>
        <p className="text-xs text-slate-500 mt-2">
          {issue.affected_rows > 0 && <><strong className="text-slate-700">{plural(issue.affected_rows, 'row')}</strong> affected · </>}
          Columns: <span className="font-mono">{issue.columns.join(', ')}</span>
        </p>
        {issue.examples.length > 0 && <ExampleTable examples={issue.examples} />}
        <p className="text-xs text-slate-700 mt-3 pt-3 border-t border-slate-100">
          <span className="font-semibold text-[#2563EB]">How to fix: </span>{issue.fix}
        </p>
      </div>
    </div>
  );
};

const ExampleTable = ({ examples }) => (
  <div className="mt-3 border border-slate-100 rounded-sm overflow-x-auto">
    <table className="w-full text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr><th className="text-left px-2.5 py-1.5 font-medium w-14">Row</th><th className="text-left px-2.5 py-1.5 font-medium">Column</th><th className="text-left px-2.5 py-1.5 font-medium">Value</th></tr>
      </thead>
      <tbody>
        {examples.map((ex, i) => (
          <tr key={`${ex.row}-${ex.column}-${i}`} className="border-t border-slate-100">
            <td className="px-2.5 py-1.5 font-mono text-slate-500">{ex.row}</td>
            <td className="px-2.5 py-1.5 text-slate-600">{ex.column}</td>
            <td className="px-2.5 py-1.5 font-mono text-slate-900 break-all">{ex.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ConfirmBar = ({ report, autofix, setAutofix, override, setOverride, reason, setReason, busy, onConfirm, onCancel, onDownload }) => {
  const blocking = report.issues.filter(i => i.severity === 'blocking');
  const anyFixable = report.issues.some(i => i.autofixable);
  const remaining = blocking.filter(i => !(autofix && i.autofixable)).length;
  const canConfirm = remaining === 0 || (override && reason.trim().length >= 5);
  return (
    <div className="sticky bottom-0 z-10 bg-white/90 backdrop-blur-md border border-slate-200 rounded-sm p-5 shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.18)]" data-testid="preflight-confirm-bar">
      <div className="flex flex-col lg:flex-row lg:items-start gap-5">
        <div className="flex-1 space-y-4">
          {anyFixable && (
            <label className="flex items-start gap-3 cursor-pointer">
              <Switch checked={autofix} onCheckedChange={setAutofix} data-testid="preflight-autofix-toggle" />
              <span>
                <span className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5"><Wand2 size={14} className="text-emerald-600" /> Auto-fix safe issues</span>
                <span className="block text-xs text-slate-500 mt-0.5">Trims spaces, strips ".0" from codes, converts Excel serial dates, drops empty columns and exact duplicate rows (keeps the first).</span>
              </span>
            </label>
          )}
          {remaining > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm space-y-2" data-testid="preflight-override-panel">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox checked={override} onCheckedChange={v => setOverride(!!v)} className="mt-0.5" data-testid="preflight-override-checkbox" />
                <span className="text-sm text-red-800">
                  <strong>Override and upload anyway.</strong> {plural(remaining, 'blocking issue')}{autofix && remaining < blocking.length ? ' that auto-fix cannot resolve' : ''} will be stored as-is. This is recorded in the audit log.
                </span>
              </label>
              {override && (
                <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
                  placeholder="Reason for override (min. 5 characters), e.g. Dates confirmed DD-MM with finance team"
                  className="bg-white text-sm" data-testid="preflight-override-reason" />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <Button variant="outline" onClick={onDownload} className="gap-2" data-testid="preflight-download-report-button"><Download size={16} /> Download full report</Button>
          <Button variant="outline" onClick={onCancel} disabled={busy} className="gap-2" data-testid="preflight-cancel-button"><X size={16} /> Cancel</Button>
          <Button onClick={onConfirm} disabled={!canConfirm || busy} className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-2 min-w-[160px]" data-testid="preflight-confirm-button">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Confirm upload
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PreflightReport = (props) => {
  const { report } = props;
  return (
    <div className="space-y-6" data-testid="preflight-report">
      <style>{`@keyframes pfIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}.pf-card-in{animation:pfIn .35s ease-out both}`}</style>
      <PreflightHeadline report={report} />
      {props.notice && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-3" data-testid="preflight-taxonomy-notice">
          <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-amber-800">{props.notice}</p>
        </div>
      )}
      {report.issues.length === 0 ? (
        <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center gap-3" data-testid="preflight-clean">
          <CheckCircle2 className="text-emerald-600" size={24} />
          <p className="text-sm text-emerald-800 font-medium">No issues found — this file is ready to upload.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="preflight-issue-list">
          {report.issues.map((issue, i) => <IssueCard key={`${issue.rule}-${issue.title}`} issue={issue} index={i} />)}
        </div>
      )}
      <ConfirmBar {...props} />
    </div>
  );
};
